import React, { useEffect, useRef } from "react";
import { ArrowDown } from "lucide-react";
import { Button } from "components/ui/button";
import { useTargeting } from "../TargetingContext";

const TOAST_MS = 6000;

// The "New distribution area added" confirmation (AC21/AC22) — shared by both
// channels. It is driven entirely by TargetingContext.justAddedArea, which only
// the user-initiated addArea sets (never hydrate/removal), so each layer gets
// the toast for free just by calling addArea. Undo reverts the add (removes the
// area); View scrolls to + focuses the new card (AC23) via highlightArea.
export const AreaAddedToast = () => {
  const { justAddedArea, clearJustAdded, highlightArea, removeArea } = useTargeting();
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // justAddedArea now outlives the step (the provider sits above the shell,
  // PAD-31560). Returning to Targeting remounts the toast, so ignore the add that
  // was already present at mount — only a genuinely new add (newer nonce) should
  // surface the confirmation again.
  const staleNonce = useRef(justAddedArea?.nonce ?? null);
  const isFresh = !!justAddedArea && justAddedArea.nonce !== staleNonce.current;

  useEffect(() => {
    if (!isFresh) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => clearJustAdded(), TOAST_MS);
    return () => clearTimeout(timer.current);
    // Re-arm whenever a new area is added (nonce changes even for repeats).
  }, [isFresh, justAddedArea, clearJustAdded]);

  if (!isFresh || !justAddedArea) return null;

  const handleUndo = () => {
    removeArea(justAddedArea.id);
    clearJustAdded();
  };

  const handleView = () => {
    highlightArea(justAddedArea.id);
    clearJustAdded();
  };

  return (
    <div
      className="absolute bottom-[15px] left-1/2 flex -translate-x-1/2 items-center rounded-[10px] border border-solid border-border bg-card px-4 py-3 shadow-[0px_10px_15px_rgba(0,0,0,0.15)]"
      data-cypress-id="targeting-area-added-alert"
    >
      {/* Spacing: [pad] arrow 8px text 8px Undo 4px View [pad] */}
      <ArrowDown className="size-4 shrink-0 text-card-foreground" />
      <p className="ml-2 whitespace-nowrap text-sm font-medium text-card-foreground">New distribution area added</p>
      <Button
        type="button"
        variant="outline"
        className="ml-2 h-6 shrink-0 px-3 text-xs"
        onClick={handleUndo}
        data-cypress-id="targeting-area-added-undo"
      >
        Undo
      </Button>
      <Button
        type="button"
        variant="outline"
        className="ml-1 h-6 shrink-0 px-3 text-xs"
        onClick={handleView}
        data-cypress-id="targeting-area-added-view"
      >
        View
      </Button>
    </div>
  );
};
